/**
 * The When input (1.O-1 deliverable 4): a combobox labelled "When" with `@` suggestions from
 * `search.picker` (canon pages only), and a live preview of what the phrase means. Every read but
 * a relative one naming a page is previewed locally; that one asks `chrono.preview` 250 ms after
 * the last keystroke for its anchor choice only, and reads the anchor's placements for the date.
 * Nothing is written here: `onSubmit` hands the read to the caller on Enter.
 */
import type { AnchorChoiceV1, DocHitV1, PlacementV1, WhenKind } from '@codex/contracts';
import { chronoPreview, searchPicker } from '@codex/contracts';
import { Icon, useLayer } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from '../auth/client.js';
import { useRoutePaths } from '../shell/sidebar-types.js';
import { usePlacements, useWhenContext, type WhenContextValue } from './calendar-context.js';
import {
  type AnchorToken,
  activeMention,
  insertAnchor,
  liveTokens,
  shiftAnchors,
} from './phrase.js';
import { placementDate } from './pill-model.js';
import { PlacementPicker } from './placement-picker.js';
import {
  type AnchorBounds,
  anchorBounds,
  anchorPlacementOf,
  isSavable,
  kindAllowed,
  readWhen,
  type WhenRead,
  type WriteChoice,
} from './when-model.js';
import {
  anchorUseText,
  boundsPrecision,
  boundsText,
  CALENDAR_FAILED,
  createAutoText,
  KIND_NOT_ALLOWED,
  NO_CALENDAR,
  PLACED_ONCE_SAVED,
  partWord,
  precisionWord,
  qualifierWord,
  reasonText,
  roleWord,
  ruleText,
  sessionRuleText,
  UNDATED_TEXT,
  UNRESOLVED_ANCHOR,
  wordAt,
} from './words.js';

export const PREVIEW_DEBOUNCE_MS = 250;
export const SUGGEST_DEBOUNCE_MS = 150;

export interface WhenInputState {
  readonly read: WhenRead | null;
  readonly choice: WriteChoice;
  /** The read can be saved: valid, allowed, its anchor picked and, for `pick`, its date chosen. */
  readonly ready: boolean;
  /** The anchor page's title as the GM picked it, for messages. */
  readonly anchorTitle: string | null;
}

export interface WhenInputProps {
  /** The page the date is for (`chrono.preview`'s `docId`). */
  readonly docId: string;
  readonly value?: string;
  readonly anchors?: readonly AnchorToken[];
  /** Prefills "now". */
  readonly defaultNow?: boolean;
  /** The kinds accepted; every kind when absent. */
  readonly allow?: readonly WhenKind[];
  readonly label?: string;
  readonly autoFocus?: boolean;
  /** A 422 `pick` at commit: shows the picker for these options. */
  readonly pick?: AnchorChoiceV1 | null;
  readonly onRead?: (state: WhenInputState) => void;
  readonly onSubmit?: (state: WhenInputState) => void;
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function WhenInput(props: WhenInputProps) {
  const ctx = useWhenContext();
  const paths = useRoutePaths();
  if (ctx.status !== 'ready' || ctx.calendar === null) {
    return (
      <div className="cx-when-input">
        <label className="cx-field__label" htmlFor={`${props.docId}-when`}>
          {props.label ?? 'When'}
        </label>
        <input
          id={`${props.docId}-when`}
          className="cx-when-input__field"
          role="combobox"
          aria-expanded={false}
          data-testid="when.input.field"
          disabled
          value=""
        />
        <p className="cx-when-input__note" role="status" data-testid="when.preview.text">
          {ctx.status === 'loading'
            ? 'Loading the calendar…'
            : ctx.status === 'error'
              ? CALENDAR_FAILED
              : NO_CALENDAR}{' '}
          {ctx.status === 'none' && paths.includes('/calendar') && (
            <a href="/calendar">Set up the calendar</a>
          )}
        </p>
      </div>
    );
  }
  return <ReadyInput {...props} ctx={ctx as Required<WhenContextValue>} />;
}

function ReadyInput({
  docId,
  value,
  anchors: initialAnchors,
  defaultNow,
  allow,
  label = 'When',
  autoFocus,
  pick,
  onRead,
  onSubmit,
  ctx,
}: WhenInputProps & { ctx: Required<WhenContextValue> }) {
  const calendar = ctx.calendar as NonNullable<WhenContextValue['calendar']>;
  const field = useRef<HTMLInputElement>(null);
  const listId = useId();
  const fieldId = useId();
  const [text, setText] = useState(value ?? (defaultNow ? 'now' : ''));
  const [anchors, setAnchors] = useState<readonly AnchorToken[]>(initialAnchors ?? []);
  const [caret, setCaret] = useState<number>(text.length);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  const read = useMemo(
    () => readWhen(text, anchors, { calendar, now: ctx.now }),
    [text, anchors, calendar, ctx.now],
  );
  const allowed = kindAllowed(read, allow);
  const relative = read.parsed.kind === 'relative' ? read.parsed : null;
  const anchorDocId = relative ? read.anchorDocId : null;
  const anchorTitle =
    anchorDocId === null
      ? null
      : (liveTokens(text, anchors).find((t) => t.docId === anchorDocId)?.label ?? null);

  // The anchor choice: one chrono.preview per settled phrase that names a page, and only where a
  // relative date is accepted (the NOW chip's input takes points only, and asks nothing).
  const asks = allow === undefined || allow.includes('relative');
  const settledInput = useDebounced(
    anchorDocId === null || !asks ? null : read.serialized,
    PREVIEW_DEBOUNCE_MS,
  );
  const previewQ = useQuery({
    queryKey: ['when', 'preview', settledInput, docId],
    queryFn: ({ signal }) =>
      api(chronoPreview, { body: { input: settledInput as string, docId } }, { signal }),
    enabled: settledInput !== null && settledInput === read.serialized,
    retry: false,
    staleTime: 10_000,
  });
  const anchorPlacements = usePlacements(asks ? anchorDocId : null);
  const choice: AnchorChoiceV1 | null =
    anchorDocId === null ? null : (pick ?? previewQ.data?.anchorChoice ?? null);
  const [pickedFor, setPickedFor] = useState(anchorDocId);
  if (pickedFor !== anchorDocId) {
    setPickedFor(anchorDocId);
    setPicked(null);
  }
  const chosenId = anchorPlacementOf(choice, picked);

  const ready =
    allowed &&
    isSavable(read) &&
    (relative === null || (choice !== null && (choice.kind !== 'pick' || picked !== null)));
  const state: WhenInputState = {
    read,
    choice: { anchorPlacementId: anchorPlacementOf(choice, picked) },
    ready,
    anchorTitle,
  };
  const stateRef = useRef(state);
  stateRef.current = state;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reported when the read or choice changes
  useEffect(() => onRead?.(stateRef.current), [read, ready, picked, choice, onRead]);

  // The @ suggestions.
  const mention = dismissed ? null : activeMention(text, caret, anchors);
  const query = useDebounced(mention?.query ?? null, SUGGEST_DEBOUNCE_MS);
  const suggestQ = useQuery({
    queryKey: ['when', 'picker', query],
    queryFn: ({ signal }) =>
      api(searchPicker, { query: { q: query as string, limit: 8 } }, { signal }),
    enabled: mention !== null && query !== null && query === mention.query,
    retry: false,
  });
  const hits: readonly DocHitV1[] =
    mention !== null && query === mention.query ? (suggestQ.data?.canon ?? []) : [];
  const listOpen = hits.length > 0;
  const current = Math.min(active, Math.max(hits.length - 1, 0));
  useLayer(listOpen, () => setDismissed(true), 'when-suggestions');

  const pendingCaret = useRef<number | null>(null);
  useEffect(() => {
    if (pendingCaret.current === null || field.current === null) return;
    field.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  });

  const choose = (hit: DocHitV1 | undefined) => {
    if (hit === undefined) return;
    const next = insertAnchor(text, caret, hit, anchors);
    setText(next.text);
    setAnchors(next.anchors);
    setCaret(next.caret);
    pendingCaret.current = next.caret;
    setActive(0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (listOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((current + step + hits.length) % hits.length);
        return;
      }
      if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) {
        event.preventDefault();
        choose(hits[current]);
        return;
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (ready) onSubmit?.(stateRef.current);
    }
  };

  const optionId = (index: number) => `${listId}-${index}`;
  return (
    <div className="cx-when-input">
      <label className="cx-field__label" htmlFor={fieldId}>
        {label}
      </label>
      <input
        ref={field}
        id={fieldId}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listOpen}
        aria-controls={listId}
        aria-activedescendant={listOpen ? optionId(current) : undefined}
        className="cx-when-input__field"
        data-testid="when.input.field"
        autoComplete="off"
        spellCheck={false}
        // biome-ignore lint/a11y/noAutofocus: the editor opens from a button and takes focus
        autoFocus={autoFocus}
        value={text}
        onChange={(event) => {
          const next = event.target.value;
          setAnchors(shiftAnchors(text, next, anchors));
          setText(next);
          setCaret(event.target.selectionStart ?? next.length);
          setDismissed(false);
          setActive(0);
        }}
        onSelect={(event) => setCaret(event.currentTarget.selectionStart ?? 0)}
        onKeyDown={onKeyDown}
      />
      <div
        id={listId}
        role="listbox"
        aria-label="Pages"
        className="cx-when-input__list"
        hidden={!listOpen}
      >
        {hits.map((hit, index) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox owns the keyboard (aria-activedescendant)
          <div
            key={hit.id}
            id={optionId(index)}
            role="option"
            tabIndex={-1}
            aria-selected={index === current}
            className="cx-when-input__option"
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => choose(hit)}
          >
            {hit.title}
          </div>
        ))}
      </div>
      <Preview
        read={read}
        allowed={allowed}
        calendar={calendar}
        anchorTitle={anchorTitle ?? (relative ? relative.anchor.label : null)}
        choice={choice}
        anchorPlacements={anchorPlacements.data ?? null}
        chosenId={chosenId}
      />
      {choice?.kind === 'pick' && (
        <PlacementPicker
          title={anchorTitle ?? relative?.anchor.label ?? ''}
          options={choice.options}
          placements={anchorPlacements.data ?? []}
          calendar={calendar}
          chosenId={picked}
          onChoose={setPicked}
        />
      )}
    </div>
  );
}

interface PreviewProps {
  readonly read: WhenRead;
  readonly allowed: boolean;
  readonly calendar: NonNullable<WhenContextValue['calendar']>;
  readonly anchorTitle: string | null;
  readonly choice: AnchorChoiceV1 | null;
  readonly anchorPlacements: readonly PlacementV1[] | null;
  readonly chosenId: string | null;
}

function Line({ children, tone }: { children: ReactNode; tone?: 'warning' }) {
  return (
    <span className="cx-when-preview__line" data-tone={tone}>
      {children}
    </span>
  );
}

function Preview(props: PreviewProps) {
  return (
    <p className="cx-when-preview" role="status" data-testid="when.preview.text">
      <PreviewLines {...props} />
    </p>
  );
}

function PreviewLines({
  read,
  allowed,
  calendar,
  anchorTitle,
  choice,
  anchorPlacements,
  chosenId,
}: PreviewProps) {
  const { preview, parsed } = read;
  const invalid = (text: string) => (
    <Line tone="warning">
      <Icon name="warning" size={14} /> {text}
    </Line>
  );
  if (!allowed) return invalid(KIND_NOT_ALLOWED);
  if (parsed.kind === 'invalid' || preview.status === 'invalid') {
    const reason =
      parsed.kind === 'invalid' ? parsed.reason : (preview.reason ?? 'unexpected_text');
    const at = parsed.kind === 'invalid' ? parsed.at : 0;
    return invalid(reasonText(reason, wordAt(read.serialized, at)));
  }
  if (parsed.kind === 'undated') return <Line>{UNDATED_TEXT}</Line>;
  if (parsed.kind === 'session') return <Line>{sessionRuleText(parsed.number)}</Line>;
  if (parsed.kind === 'relative') {
    const title = anchorTitle ?? parsed.anchor.label;
    const rule = <Line>{ruleText(parsed, title)}</Line>;
    if (read.anchorDocId === null) {
      return (
        <>
          {rule}
          {invalid(UNRESOLVED_ANCHOR)}
        </>
      );
    }
    if (choice?.kind === 'create_auto') {
      return (
        <>
          {rule}
          <Line>{createAutoText(title)}</Line>
        </>
      );
    }
    const anchor = anchorPlacements?.find((p) => p.id === chosenId) ?? null;
    if (anchor === null) return rule;
    const bounds: AnchorBounds | null = anchorBounds(read, anchor, calendar);
    const boundLine =
      bounds === null
        ? PLACED_ONCE_SAVED
        : (boundsText(calendar, bounds.earliest, bounds.latest, bounds.precision) ??
          PLACED_ONCE_SAVED);
    return (
      <>
        {rule}
        <Line>
          {anchorUseText(anchor.label ?? roleWord(anchor.role), placementDate(calendar, anchor))}
        </Line>
        <Line>{boundLine}</Line>
      </>
    );
  }
  const point = parsed.kind === 'point' ? parsed : null;
  const part = point?.loParts.part ?? point?.hiParts?.part;
  const words = [
    preview.precision ? precisionWord(preview.precision) : null,
    partWord(part),
    preview.qualifier ? qualifierWord(preview.qualifier) : null,
  ].filter((w): w is string => w !== null);
  const precision = boundsPrecision(preview.precision ?? 'day', part);
  const bounds = boundsText(calendar, preview.earliest, preview.latest, precision, 'point');
  return (
    <>
      <Line>
        <strong>{preview.canonical ?? read.text}</strong>
      </Line>
      <Line>
        {words.map((w) => (
          <span key={w} className="cx-when-preview__word">
            {w}
          </span>
        ))}
      </Line>
      {bounds && <Line>{bounds}</Line>}
    </>
  );
}
